import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FileUp, X, Loader2, ScanLine, Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const fade = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

export default function PrescriptionUpload({ onAdd }) {
  const [open, setOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [checked, setChecked] = useState([]);
  const fileRef = useRef(null);

  const scan = async (file) => {
    if (!file) return;
    setScanning(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/prescription/scan", fd);
      setResult(data);
      setChecked(data.items.map((i) => i.id));
      if (data.items.length === 0) toast.info(data.notes || "No matching tests found in this prescription");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setScanning(false);
    }
  };

  const addToCart = () => {
    onAdd(checked);
    setOpen(false);
    setResult(null);
    toast.success(`${checked.length} test${checked.length !== 1 ? "s" : ""} added to your cart`);
  };

  return (
    <section className="bg-white pt-20 lg:pt-28 -mb-20 lg:-mb-28 relative z-10" data-testid="prescription-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }}
          className="relative overflow-hidden rounded-[1.75rem] bg-gradient-to-r from-[#0F2942] to-[#0B192C] grain p-8 lg:p-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 border border-white/10">
          <div className="absolute inset-0 dot-grid opacity-50" />
          <div className="relative flex items-start gap-5">
            <div className="w-12 h-12 rounded-2xl bg-[#D92638]/20 flex items-center justify-center shrink-0">
              <ScanLine className="w-6 h-6 text-[#D92638]" />
            </div>
            <div>
              <h3 className="text-xl lg:text-2xl font-bold text-white">Have a doctor's prescription?</h3>
              <p className="mt-1.5 text-sm text-slate-300 max-w-xl leading-relaxed">
                Upload a photo — our AI reads it, finds the tests your doctor ordered, and adds them to your cart in seconds.
              </p>
            </div>
          </div>
          <button onClick={() => setOpen(true)} data-testid="upload-prescription-button"
            className="relative shrink-0 flex items-center gap-2.5 bg-white text-[#0B192C] font-semibold text-sm px-6 py-3.5 rounded-full transition-all duration-300 hover:-translate-y-0.5 active:scale-95 shadow-lg">
            <FileUp className="w-4 h-4 text-[#D92638]" /> Upload prescription
          </button>
        </motion.div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-[#0B192C]/70 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-6" onClick={() => setOpen(false)}>
            <motion.div initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 50, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white w-full max-w-lg max-h-[88vh] overflow-y-auto rounded-t-[2rem] sm:rounded-[2rem] p-7" data-testid="prescription-modal">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-bold text-lg text-[#0B192C]">Scan prescription</h3>
                <button onClick={() => setOpen(false)} aria-label="Close" data-testid="prescription-close"
                  className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500"><X className="w-5 h-5" /></button>
              </div>

              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="prescription-file-input"
                onChange={(e) => { scan(e.target.files[0]); e.target.value = ""; }} />

              {!result && !scanning && (
                <button onClick={() => fileRef.current?.click()} data-testid="prescription-dropzone"
                  className="w-full border-2 border-dashed border-slate-300 hover:border-[#0D9488] rounded-2xl py-14 flex flex-col items-center gap-3 text-slate-500 hover:text-[#0D9488] transition-colors">
                  <FileUp className="w-8 h-8" />
                  <span className="text-sm font-medium">Tap to upload a photo of the prescription</span>
                  <span className="text-xs text-slate-400">JPG or PNG, up to 10 MB</span>
                </button>
              )}

              {scanning && (
                <div className="py-14 flex flex-col items-center gap-4 text-slate-500" data-testid="prescription-scanning">
                  <Loader2 className="w-8 h-8 animate-spin text-[#D92638]" />
                  <span className="text-sm font-medium">Hema is reading your prescription...</span>
                </div>
              )}

              {result && (
                <div>
                  {result.items.length > 0 ? (
                    <>
                      <p className="text-sm text-slate-500 mb-4">We found these tests in your prescription:</p>
                      <div className="space-y-2 max-h-[38vh] overflow-y-auto pr-1">
                        {result.items.map((item) => (
                          <button key={item.id} onClick={() => setChecked((p) => (p.includes(item.id) ? p.filter((x) => x !== item.id) : [...p, item.id]))}
                            data-testid={`rx-item-${item.id}`}
                            className={`w-full flex items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left transition-all ${checked.includes(item.id) ? "border-[#0D9488] bg-[#0D9488]/[0.05]" : "border-slate-200"}`}>
                            <span className="text-sm font-medium text-[#0B192C]">{item.name}</span>
                            <span className="flex items-center gap-3 shrink-0">
                              <span className="font-mono-med text-sm font-semibold">₹{item.price.toLocaleString("en-IN")}</span>
                              <span className={`w-5 h-5 rounded-full flex items-center justify-center ${checked.includes(item.id) ? "bg-[#0D9488] text-white" : "border border-slate-300 text-transparent"}`}>
                                <Check className="w-3 h-3" />
                              </span>
                            </span>
                          </button>
                        ))}
                      </div>
                      {result.other_tests?.length > 0 && (
                        <p className="mt-3 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5">
                          Also on your prescription (not in our online catalog): {result.other_tests.join(", ")} — WhatsApp us to book these.
                        </p>
                      )}
                      <button onClick={addToCart} disabled={checked.length === 0} data-testid="rx-add-to-cart"
                        className="mt-5 w-full flex items-center justify-center gap-2 bg-[#D92638] hover:bg-[#B81D2D] text-white font-semibold py-3.5 rounded-full transition-all active:scale-[0.98] disabled:opacity-50">
                        Add {checked.length} test{checked.length !== 1 ? "s" : ""} to cart <ArrowRight className="w-4 h-4" />
                      </button>
                    </>
                  ) : (
                    <div className="py-8 text-center">
                      <p className="text-sm text-slate-500">{result.notes || "Couldn't read this prescription."}</p>
                      <button onClick={() => { setResult(null); fileRef.current?.click(); }} data-testid="rx-retry"
                        className="mt-4 text-sm font-semibold text-[#0D9488] hover:underline">Try a clearer photo</button>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
